import { useEffect, useMemo, useState } from 'react';
import useSwr from 'swr';
import { useRouter } from 'next/router';
import {
  Breed,
  Coupon,
  ErrorResponse,
  PreferredIngredientObject,
  Recommendation,
  RecommendationApiBreedsResponse,
  RecommendationApiResponse,
  RecommendationApiSpecialNeedsResponse,
  RecommendationApiPreferredIngredientsResponse,
  SpecialNeedsObject,
} from '../types';
import { noAuthFetcher } from './fetcher';
import {
  useAccount,
  useIsLoggedIn,
  useLocale,
  useShippingCountry,
} from './hooks';
import {
  getRecommendationInitialValues,
  saveOnRecommendationState,
} from './utils.recommendation';
import { usePersistedState } from './hooks.persist';
import { isCoupon } from '@lib/utils.coupon';
import { checkAlvarref } from './api/coupon';
import { WizardFormValues } from '../components/forms/Recommendation/types';
import specialNeeds from 'constants/specialNeeds';
import ingredients from 'constants/ingredients';

interface RecommendationProps {
  recommendation?: Recommendation;
  loading: boolean;
}

export const fetchRecommendation = (query: string) =>
  fetch(`/api/recommendation?${query}`).then((res) => res.json());

export function useRecommendation(drqs: string): RecommendationProps {
  const locale = useLocale();
  const { data, error, ...rest } = useSwr<RecommendationApiResponse>(
    `type=recommendation&lang=${locale}${drqs && `&${drqs}`}`,
    fetchRecommendation,
    { revalidateOnFocus: false },
  );

  if ((data as ErrorResponse)?.error || error || (data as any)?.errors) {
    return {
      recommendation: undefined,
      loading: false,
      ...rest,
    };
  }

  return {
    recommendation: data as Recommendation,
    loading: !data,
    ...rest,
  };
}

const mapBreeds = (arr: Breed[]) => arr.filter(({ name }) => !!name);

export function useBreeds(): { breeds?: Breed[]; loading: boolean } {
  const locale = useLocale();
  const { data, ...rest } = useSwr<RecommendationApiBreedsResponse>(
    `/api/recommendation?type=breeds&lang=${locale}`,
    noAuthFetcher,
    { revalidateOnFocus: false },
  );

  if ((data as ErrorResponse)?.error) {
    return {
      loading: false,
      ...rest,
    };
  }

  return {
    breeds: data && mapBreeds(data as Breed[]),
    loading: !data,
    ...rest,
  };
}

export const usePreferredIngredients = (): PreferredIngredientObject[] => {
  const locale = useLocale();
  const fallback = ingredients;
  const { data } = useSwr<RecommendationApiPreferredIngredientsResponse>(
    `/api/recommendation?type=preferredIngredients&lang=${locale}`,
    noAuthFetcher,
    { revalidateOnFocus: false },
  );

  if ((data as ErrorResponse)?.error) {
    return fallback;
  }

  return (data as PreferredIngredientObject[]) || fallback;
};

export function useSpecialNeeds(): SpecialNeedsObject[] {
  const locale = useLocale();
  const fallback = specialNeeds;
  const { data } = useSwr<RecommendationApiSpecialNeedsResponse>(
    `/api/recommendation?type=specialNeeds&lang=${locale}`,
    noAuthFetcher,
    { revalidateOnFocus: false },
  );

  if ((data as ErrorResponse)?.error) {
    return fallback;
  }

  return (data as SpecialNeedsObject[]) || fallback;
}

export const useAlvarref = (): {
  alvarref: string | undefined;
  referralCoupon: Coupon | undefined;
  loading: boolean;
} => {
  const {
    query: { alvarref: queryStringRef },
  } = useRouter();
  const { isLoggedIn } = useIsLoggedIn();
  const { country } = useShippingCountry();
  const [alvarref, setAlvarref] = usePersistedState<string | undefined>(
    'alvarref',
    undefined,
  );
  const [referralCoupon, setReferralCoupon] = usePersistedState<
    Coupon | undefined
  >('referralCoupon', undefined);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isLoggedIn && queryStringRef && country) {
      if (!alvarref || alvarref !== queryStringRef) {
        (async () => {
          setLoading(true);
          const referralCoupon = await checkAlvarref({
            alvarref: queryStringRef as string,
            country,
          });
          if (isCoupon(referralCoupon)) {
            setAlvarref(queryStringRef as string);
            saveOnRecommendationState({
              aw_user_is_referral: queryStringRef as string,
            });
            setReferralCoupon(referralCoupon);
          } else if (alvarref) {
            setAlvarref(undefined);
          }
          setLoading(false);
        })();
      }
    }
  }, [queryStringRef, isLoggedIn, alvarref, setAlvarref, country]);

  return {
    alvarref: isLoggedIn ? undefined : alvarref || undefined,
    referralCoupon,
    loading: isLoggedIn ? false : loading,
  };
};

export const useRecommendationInitialValues = (
  additional: Partial<WizardFormValues> = {},
) => {
  const locale = useLocale();
  const { account } = useAccount();

  const initialValues = useMemo(
    () =>
      getRecommendationInitialValues({
        locale,
        account,
        ...additional,
      }),
    [locale, account, additional],
  );
  return initialValues;
};
